import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import { NOTIFICATION_VARIANT, STATUS } from "@/constants";
import {
  SEMANTIC_GENERATION_STATUS,
  isDomainBusinessContext,
  isDomainDetail,
  isDomainMetadataResponse,
  isDomainRecord,
  type DomainBusinessContext,
  type DomainDetail,
  type DomainMetadata,
  type DomainRecord,
  type SemanticModelSection,
} from "@/features/data-hub/model/domainDetail";
import type { DomainEntitiesPayload } from "@/features/data-hub/model/domainEntities";
import type { SemanticCandidate } from "@/features/data-hub/model/semanticCandidates";
import type { SemanticDimension } from "@/features/data-hub/model/semanticDimensions";
import type { SemanticEntity } from "@/features/data-hub/model/semanticEntities";
import type { SemanticGlossaryTerm } from "@/features/data-hub/model/semanticGlossary";
import type { SemanticOntology } from "@/features/data-hub/model/semanticOntologies";
import type { SemanticMeasure } from "@/features/data-hub/model/semanticMeasures";
import type { SemanticMetric } from "@/features/data-hub/model/semanticMetrics";
import type { SemanticRelationship } from "@/features/data-hub/model/semanticRelationships";
import type { SemanticRule } from "@/features/data-hub/model/semanticRules";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";
import { showNotification } from "@/shared/notifications/model/notificationSlice";

type DomainDetailStatus =
  | typeof STATUS.IDLE
  | typeof STATUS.LOADING
  | typeof STATUS.SUCCEEDED
  | typeof STATUS.FAILED;

export type ClarificationChatTurn = {
  role: string;
  content: string;
  kind: string;
  refs: string[];
  created_at: string;
};

export type ClarificationChatStateShape = {
  transcript: ClarificationChatTurn[];
  current_question_id: string;
  pending_open_ids: string[];
  answered_target_keys: string[];
  skipped_target_keys: string[];
  session_started_at: string;
  last_message_at: string;
  archived_sessions: Array<{
    session_started_at: string;
    archived_at: string;
    transcript: ClarificationChatTurn[];
  }>;
  session_start_needs_input_count: number;
};

export const emptyClarificationChatState: ClarificationChatStateShape = {
  transcript: [],
  current_question_id: "",
  pending_open_ids: [],
  answered_target_keys: [],
  skipped_target_keys: [],
  session_started_at: "",
  last_message_at: "",
  archived_sessions: [],
  session_start_needs_input_count: 0,
};

export type SemanticProposal = {
  id: string;
  target_type: string;
  target_key: string;
  proposed_value: Record<string, unknown>;
  confidence: number;
  rationale: string;
  status: string;
  created_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
};

function isSemanticProposal(value: unknown): value is SemanticProposal {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    typeof obj.target_type === "string" &&
    typeof obj.target_key === "string" &&
    typeof obj.status === "string"
  );
}

export type DomainDetailState = {
  status: DomainDetailStatus;
  error: string | null;
  domainId: number | null;
  domain: DomainRecord | null;
  metadata: DomainMetadata | null;
  metadataStatus: DomainDetailStatus;
  metadataError: string | null;
  businessContext: DomainBusinessContext | null;
  businessContextStatus: DomainDetailStatus;
  businessContextError: string | null;
  businessContextPatchStatus: DomainDetailStatus;
  businessContextPatchError: string | null;
  isRefreshing: boolean;
  entitiesPatchStatus: DomainDetailStatus;
  entitiesPatchError: string | null;
  semanticEntitiesPatchStatus: DomainDetailStatus;
  semanticEntitiesPatchError: string | null;
  semanticRelationshipsPatchStatus: DomainDetailStatus;
  semanticRelationshipsPatchError: string | null;
  semanticMeasuresPatchStatus: DomainDetailStatus;
  semanticMeasuresPatchError: string | null;
  semanticMetricsPatchStatus: DomainDetailStatus;
  semanticMetricsPatchError: string | null;
  semanticRulesPatchStatus: DomainDetailStatus;
  semanticRulesPatchError: string | null;
  semanticGlossaryPatchStatus: DomainDetailStatus;
  semanticGlossaryPatchError: string | null;
  semanticOntologiesPatchStatus: DomainDetailStatus;
  semanticOntologiesPatchError: string | null;
  semanticDimensionsPatchStatus: DomainDetailStatus;
  semanticDimensionsPatchError: string | null;
  semanticCandidatesPatchStatus: DomainDetailStatus;
  semanticCandidatesPatchError: string | null;
  publishStatus: DomainDetailStatus;
  publishError: string | null;
  rerunStatus: DomainDetailStatus;
  rerunError: string | null;
  syncIndexesStatus: DomainDetailStatus;
  syncIndexesError: string | null;
  chatState: ClarificationChatStateShape;
  chatStatus: DomainDetailStatus;
  chatError: string | null;
  remainingItems: RemainingInterviewItem[];
  remainingItemsStatus: DomainDetailStatus;
  proposals: SemanticProposal[];
  proposalsStatus: DomainDetailStatus;
  proposalsError: string | null;
  proposalReviewStatus: DomainDetailStatus;
  proposalReviewError: string | null;
  itemReviewStatus: DomainDetailStatus;
  itemReviewError: string | null;
  sectionReviewStatus: DomainDetailStatus;
  sectionReviewError: string | null;
};

export const domainDetailInitialState: DomainDetailState = {
  status: STATUS.IDLE,
  error: null,
  domainId: null,
  domain: null,
  metadata: null,
  metadataStatus: STATUS.IDLE,
  metadataError: null,
  businessContext: null,
  businessContextStatus: STATUS.IDLE,
  businessContextError: null,
  businessContextPatchStatus: STATUS.IDLE,
  businessContextPatchError: null,
  isRefreshing: false,
  entitiesPatchStatus: STATUS.IDLE,
  entitiesPatchError: null,
  semanticEntitiesPatchStatus: STATUS.IDLE,
  semanticEntitiesPatchError: null,
  semanticRelationshipsPatchStatus: STATUS.IDLE,
  semanticRelationshipsPatchError: null,
  semanticMeasuresPatchStatus: STATUS.IDLE,
  semanticMeasuresPatchError: null,
  semanticMetricsPatchStatus: STATUS.IDLE,
  semanticMetricsPatchError: null,
  semanticRulesPatchStatus: STATUS.IDLE,
  semanticRulesPatchError: null,
  semanticGlossaryPatchStatus: STATUS.IDLE,
  semanticGlossaryPatchError: null,
  semanticOntologiesPatchStatus: STATUS.IDLE,
  semanticOntologiesPatchError: null,
  semanticDimensionsPatchStatus: STATUS.IDLE,
  semanticDimensionsPatchError: null,
  semanticCandidatesPatchStatus: STATUS.IDLE,
  semanticCandidatesPatchError: null,
  publishStatus: STATUS.IDLE,
  publishError: null,
  rerunStatus: STATUS.IDLE,
  rerunError: null,
  syncIndexesStatus: STATUS.IDLE,
  syncIndexesError: null,
  chatState: emptyClarificationChatState,
  chatStatus: STATUS.IDLE,
  chatError: null,
  remainingItems: [],
  remainingItemsStatus: STATUS.IDLE,
  proposals: [],
  proposalsStatus: STATUS.IDLE,
  proposalsError: null,
  proposalReviewStatus: STATUS.IDLE,
  proposalReviewError: null,
  itemReviewStatus: STATUS.IDLE,
  itemReviewError: null,
  sectionReviewStatus: STATUS.IDLE,
  sectionReviewError: null,
};

export const fetchDomainById = createAsyncThunk<
  DomainRecord,
  number,
  { rejectValue: string }
>(
  "domainDetail/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${id}`, { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainRecord(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition: (id, { getState }) => {
      const { domainDetail } = getState() as RootState;
      if (domainDetail.domainId !== id) {
        return true;
      }
      if (domainDetail.status === STATUS.LOADING) {
        return false;
      }
      if (domainDetail.status === STATUS.SUCCEEDED && domainDetail.domain !== null) {
        return false;
      }
      return true;
    },
  },
);

/**
 * Silent refetch for polling. Unlike :func:`fetchDomainById`, this thunk does not flip
 * the page-level status back to ``loading`` (so the skeleton/loader doesn't flash) and
 * does not clear the in-memory domain while the new payload is in flight.
 */
export const refreshDomainById = createAsyncThunk<
  DomainRecord,
  number,
  { rejectValue: string }
>(
  "domainDetail/refreshById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${id}`, { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainRecord(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition: (id, { getState }) => {
      const { domainDetail } = getState() as RootState;
      if (domainDetail.isRefreshing) {
        return false;
      }
      return domainDetail.domainId === id;
    },
  },
);

export const fetchDomainMetadata = createAsyncThunk<
  DomainMetadata,
  number,
  { rejectValue: string }
>(
  "domainDetail/fetchMetadata",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${id}/metadata`, { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainMetadataResponse(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data.metadata;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition: (id, { getState }) => {
      const { domainDetail } = getState() as RootState;
      if (domainDetail.domainId !== id) {
        return true;
      }
      if (domainDetail.metadataStatus === STATUS.LOADING) {
        return false;
      }
      if (
        domainDetail.metadataStatus === STATUS.SUCCEEDED &&
        domainDetail.metadata !== null
      ) {
        return false;
      }
      return true;
    },
  },
);

export const fetchDomainBusinessContext = createAsyncThunk<
  DomainBusinessContext,
  number,
  { rejectValue: string }
>("domainDetail/fetchBusinessContext", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/business-context`, {
      method: "GET",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isDomainBusinessContext(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type PatchDomainBusinessContextArgs = {
  domainId: number;
  businessContext: DomainBusinessContext;
};

export const patchDomainBusinessContext = createAsyncThunk<
  DomainBusinessContext,
  PatchDomainBusinessContextArgs,
  { rejectValue: string }
>(
  "domainDetail/patchBusinessContext",
  async ({ domainId, businessContext }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/business-context`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(businessContext),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainBusinessContext(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export type PatchDomainDraftEntitiesArgs = {
  domainId: number;
  draftEntities: DomainEntitiesPayload;
  signal?: AbortSignal;
};

export const patchDomainDraftEntities = createAsyncThunk<
  void,
  PatchDomainDraftEntitiesArgs,
  { rejectValue: string }
>(
  "domainDetail/patchDraftEntities",
  async ({ domainId, draftEntities, signal }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draft_entities: draftEntities }),
        signal,
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      dispatch(
        showNotification({
          variant: NOTIFICATION_VARIANT.SUCCESS,
          messageKey: "domainDetail.canvas.saveSuccess",
          messageNs: "dataHub",
        }),
      );
      return;
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        return rejectWithValue("Request aborted");
      }
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

type PatchSemanticSectionResult =
  | { ok: true; data: DomainDetail }
  | { ok: false; error: string };

/**
 * Shared request body for the unified `PATCH /domains/:id/semantic-model` endpoint.
 * All 7 section-specific thunks below delegate here instead of hitting their own
 * per-type route, so there is one HTTP call site to maintain instead of 7.
 */
async function patchSemanticSection(
  domainId: number,
  section: SemanticModelSection,
  items: readonly Record<string, unknown>[],
): Promise<PatchSemanticSectionResult> {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/semantic-model`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ section, items }),
    });
    if (!res.ok) {
      return { ok: false, error: await readApiErrorMessage(res) };
    }
    const data: unknown = await res.json();
    if (!isDomainDetail(data)) {
      return { ok: false, error: "Unexpected response shape" };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Request failed" };
  }
}

type SemanticSectionPatchAction = "save" | "delete";

function notifySemanticSectionSaved(
  dispatch: (action: ReturnType<typeof showNotification>) => void,
  action: SemanticSectionPatchAction = "save",
): void {
  dispatch(
    showNotification({
      variant: NOTIFICATION_VARIANT.SUCCESS,
      messageKey:
        action === "delete"
          ? "domainDetail.canvas.deleteSuccess"
          : "domainDetail.canvas.saveSuccess",
      messageNs: "dataHub",
    }),
  );
}

export type PatchDomainSemanticEntitiesArgs = {
  domainId: number;
  entities: SemanticEntity[];
};

export const patchDomainSemanticEntities = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticEntitiesArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticEntities",
  async ({ domainId, entities }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "entities", entities);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PatchDomainSemanticRelationshipsArgs = {
  domainId: number;
  relationships: SemanticRelationship[];
};

export const patchDomainSemanticRelationships = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticRelationshipsArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticRelationships",
  async ({ domainId, relationships }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "relationships", relationships);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PatchDomainSemanticMeasuresArgs = {
  domainId: number;
  measures: SemanticMeasure[];
};

export const patchDomainSemanticMeasures = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticMeasuresArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticMeasures",
  async ({ domainId, measures }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "measures", measures);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PatchDomainSemanticMetricsArgs = {
  domainId: number;
  metrics: SemanticMetric[];
};

export const patchDomainSemanticMetrics = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticMetricsArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticMetrics",
  async ({ domainId, metrics }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "metrics", metrics);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PatchDomainSemanticRulesArgs = {
  domainId: number;
  rules: SemanticRule[];
  action?: SemanticSectionPatchAction;
};

export const patchDomainSemanticRules = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticRulesArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticRules",
  async ({ domainId, rules, action = "save" }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "rules", rules);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch, action);
    return result.data;
  },
);

export type PatchDomainSemanticGlossaryArgs = {
  domainId: number;
  businessGlossary: SemanticGlossaryTerm[];
  action?: SemanticSectionPatchAction;
};

export const patchDomainSemanticGlossary = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticGlossaryArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticGlossary",
  async ({ domainId, businessGlossary, action = "save" }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "glossary", businessGlossary);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch, action);
    return result.data;
  },
);

export type PatchDomainSemanticOntologiesArgs = {
  domainId: number;
  ontologies: SemanticOntology[];
};

export const patchDomainSemanticOntologies = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticOntologiesArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticOntologies",
  async ({ domainId, ontologies }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "ontologies", ontologies);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PatchDomainSemanticDimensionsArgs = {
  domainId: number;
  dimensions: SemanticDimension[];
};

export const patchDomainSemanticDimensions = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticDimensionsArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticDimensions",
  async ({ domainId, dimensions }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "dimensions", dimensions);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PatchDomainSemanticCandidatesArgs = {
  domainId: number;
  candidates: SemanticCandidate[];
};

export const patchDomainSemanticCandidates = createAsyncThunk<
  DomainDetail,
  PatchDomainSemanticCandidatesArgs,
  { rejectValue: string }
>(
  "domainDetail/patchSemanticCandidates",
  async ({ domainId, candidates }, { rejectWithValue, dispatch }) => {
    const result = await patchSemanticSection(domainId, "candidates", candidates);
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    notifySemanticSectionSaved(dispatch);
    return result.data;
  },
);

export type PublishDomainArgs = {
  domainId: number;
  draftEntities: DomainEntitiesPayload;
};

export type PublishDomainResult = {
  domain: DomainDetail | null;
  draftEntities: DomainEntitiesPayload;
};

export const publishDomain = createAsyncThunk<
  PublishDomainResult,
  PublishDomainArgs,
  { rejectValue: string }
>(
  "domainDetail/publish",
  async ({ domainId, draftEntities }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ published: true }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const body = await res.text();
      let domain: DomainDetail | null = null;
      if (body.length > 0) {
        const data: unknown = JSON.parse(body);
        if (!isDomainDetail(data)) {
          return rejectWithValue("Unexpected response shape");
        }
        domain = data;
      }
      dispatch(
        showNotification({
          variant: NOTIFICATION_VARIANT.SUCCESS,
          messageKey: "domainDetail.publish.success",
          messageNs: "dataHub",
        }),
      );
      return { domain, draftEntities };
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export type RerunDomainEnrichmentArgs = {
  domainId: number;
};

export type SyncSemanticIndexesArgs = {
  domainId: number;
};

export const syncSemanticIndexes = createAsyncThunk<
  DomainDetail,
  SyncSemanticIndexesArgs,
  { rejectValue: string }
>(
  "domainDetail/syncSemanticIndexes",
  async ({ domainId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiFetch(
        `/v1/domains/${domainId}/semantic-model/sync-indexes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      );
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainDetail(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      dispatch(
        showNotification({
          variant: NOTIFICATION_VARIANT.SUCCESS,
          messageKey: "domainDetail.semanticModel.syncIndexes.succeeded",
          messageNs: "dataHub",
        }),
      );
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const rerunDomainEnrichment = createAsyncThunk<
  DomainDetail,
  RerunDomainEnrichmentArgs,
  { rejectValue: string }
>(
  "domainDetail/rerunEnrichment",
  async ({ domainId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/enrichment/rerun`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainDetail(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      dispatch(
        showNotification({
          variant: NOTIFICATION_VARIANT.SUCCESS,
          messageKey: "domainDetail.semanticModel.rerun.queued",
          messageNs: "dataHub",
        }),
      );
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

function normalizeClarificationChatState(
  value: unknown,
): ClarificationChatStateShape | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.transcript) || !Array.isArray(obj.archived_sessions)) {
    return null;
  }
  return {
    transcript: obj.transcript as ClarificationChatTurn[],
    current_question_id:
      typeof obj.current_question_id === "string" ? obj.current_question_id : "",
    pending_open_ids: Array.isArray(obj.pending_open_ids)
      ? obj.pending_open_ids.filter((id): id is string => typeof id === "string")
      : [],
    answered_target_keys: Array.isArray(obj.answered_target_keys)
      ? obj.answered_target_keys.filter((key): key is string => typeof key === "string")
      : [],
    skipped_target_keys: Array.isArray(obj.skipped_target_keys)
      ? obj.skipped_target_keys.filter((key): key is string => typeof key === "string")
      : [],
    session_started_at:
      typeof obj.session_started_at === "string" ? obj.session_started_at : "",
    last_message_at:
      typeof obj.last_message_at === "string" ? obj.last_message_at : "",
    archived_sessions: obj.archived_sessions as ClarificationChatStateShape["archived_sessions"],
    session_start_needs_input_count:
      typeof obj.session_start_needs_input_count === "number"
        ? obj.session_start_needs_input_count
        : 0,
  };
}

function clarificationChatFromGeneratedModel(
  generatedModel: Record<string, unknown>,
): ClarificationChatStateShape | null {
  return normalizeClarificationChatState(generatedModel.clarification_chat);
}

function turnsMatch(a: ClarificationChatTurn, b: ClarificationChatTurn): boolean {
  return (
    a.role === b.role &&
    a.content === b.content &&
    a.kind === b.kind &&
    a.created_at === b.created_at
  );
}

function mergeLatestTurnIntoChatState(
  state: ClarificationChatStateShape,
  latestTurn: ClarificationChatTurn | null,
): ClarificationChatStateShape {
  if (latestTurn === null) {
    return state;
  }
  const last = state.transcript[state.transcript.length - 1];
  if (last !== undefined && turnsMatch(last, latestTurn)) {
    return state;
  }
  if (state.transcript.some((turn) => turnsMatch(turn, latestTurn))) {
    return state;
  }
  return {
    ...state,
    transcript: [...state.transcript, latestTurn],
    last_message_at: latestTurn.created_at || state.last_message_at,
  };
}

function chatStateFromResponse(parsed: ChatStateResponse): ClarificationChatStateShape {
  return mergeLatestTurnIntoChatState(parsed.state, parsed.latest_turn);
}

function applyDomainDetailPayload(
  state: DomainDetailState,
  payload: DomainDetail,
): void {
  const { metadata, ...domain } = payload;
  state.domain = domain;
  state.domainId = domain.id;
  state.metadata = metadata;
}

function syncDomainGeneratedModelChat(
  domain: DomainRecord,
  chatState: ClarificationChatStateShape,
  document: Record<string, unknown> | null,
): DomainRecord {
  const generated = {
    ...domain.generated_model,
    clarification_chat: chatState,
    ...(document ? { document } : {}),
  };
  return { ...domain, generated_model: generated };
}

export type ChatStateResponse = {
  state: ClarificationChatStateShape;
  latest_turn: ClarificationChatTurn | null;
  document: Record<string, unknown> | null;
};

function parseChatStateResponse(value: unknown): ChatStateResponse | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const obj = value as Record<string, unknown>;
  const state = normalizeClarificationChatState(obj.state);
  if (state === null) {
    return null;
  }
  const latestRaw = obj.latest_turn;
  let latest: ClarificationChatTurn | null = null;
  if (latestRaw !== null && typeof latestRaw === "object") {
    const t = latestRaw as Record<string, unknown>;
    if (
      typeof t.role === "string" &&
      typeof t.content === "string" &&
      typeof t.kind === "string" &&
      Array.isArray(t.refs) &&
      typeof t.created_at === "string"
    ) {
      latest = {
        role: t.role,
        content: t.content,
        kind: t.kind,
        refs: t.refs.filter((r): r is string => typeof r === "string"),
        created_at: t.created_at,
      };
    }
  }
  const documentRaw = obj.document;
  const document =
    documentRaw !== null && typeof documentRaw === "object"
      ? (documentRaw as Record<string, unknown>)
      : null;
  return { state, latest_turn: latest, document };
}

export const fetchClarificationChatState = createAsyncThunk<
  ClarificationChatStateShape,
  number,
  { rejectValue: string }
>(
  "domainDetail/fetchClarificationChat",
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/clarifications/chat`, {
        method: "GET",
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      const parsed = parseChatStateResponse(data);
      if (parsed === null) {
        return rejectWithValue("Unexpected response shape");
      }
      return chatStateFromResponse(parsed);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export type SendClarificationChatMessageArgs = {
  domainId: number;
  message: string;
  skipCurrent?: boolean;
  focusedKind?: string;
  focusedTargetRef?: Record<string, unknown>;
};

export type AskAboutTargetArgs = {
  domainId: number;
  kind: string;
  targetRef: Record<string, unknown>;
};

export type RemainingInterviewItem = {
  kind: string;
  target_ref: Record<string, unknown>;
  label: string;
  detail?: string;
};

export const sendClarificationChatMessage = createAsyncThunk<
  ChatStateResponse,
  SendClarificationChatMessageArgs,
  { rejectValue: string }
>(
  "domainDetail/sendClarificationChat",
  async ({ domainId, message, skipCurrent, focusedKind, focusedTargetRef }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/clarifications/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          skip_current: Boolean(skipCurrent),
          focused_kind: focusedKind ?? "",
          focused_target_ref: focusedTargetRef ?? {},
        }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      const parsed = parseChatStateResponse(data);
      if (parsed === null) {
        return rejectWithValue("Unexpected response shape");
      }
      return parsed;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const askAboutTarget = createAsyncThunk<
  ChatStateResponse,
  AskAboutTargetArgs,
  { rejectValue: string }
>(
  "domainDetail/askAboutTarget",
  async ({ domainId, kind, targetRef }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/interview/ask-about`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, target_ref: targetRef }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      const parsed = parseChatStateResponse(data);
      if (parsed === null) {
        return rejectWithValue("Unexpected response shape");
      }
      return parsed;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const reviewSkippedQuestions = createAsyncThunk<
  ChatStateResponse,
  number,
  { rejectValue: string }
>(
  "domainDetail/reviewSkippedQuestions",
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/interview/review-skipped`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      const parsed = parseChatStateResponse(data);
      if (parsed === null) {
        return rejectWithValue("Unexpected response shape");
      }
      return parsed;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchRemainingItems = createAsyncThunk<
  RemainingInterviewItem[],
  number,
  { rejectValue: string }
>(
  "domainDetail/fetchRemainingItems",
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/interview/remaining-items`);
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!Array.isArray(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data as RemainingInterviewItem[];
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

/**
 * Staged LLM proposals (core doc principle 5): a regeneration found a fresh value
 * conflicting with something a human already curated, and staged it here instead of
 * silently overwriting the curated item.
 */
export type FetchDomainSemanticProposalsArgs = {
  domainId: number;
  status?: string;
};

export const fetchDomainSemanticProposals = createAsyncThunk<
  SemanticProposal[],
  FetchDomainSemanticProposalsArgs,
  { rejectValue: string }
>(
  "domainDetail/fetchSemanticProposals",
  async ({ domainId, status = "pending" }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ proposal_status: status });
      const res = await apiFetch(
        `/v1/domains/${domainId}/semantic-model/proposals?${params.toString()}`,
      );
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!Array.isArray(data) || !data.every(isSemanticProposal)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

type ReviewSemanticProposalArgs = {
  domainId: number;
  proposalId: string;
  reviewedBy: string;
};

async function reviewSemanticProposal(
  domainId: number,
  proposalId: string,
  reviewedBy: string,
  action: "accept" | "reject",
): Promise<PatchSemanticSectionResult> {
  try {
    const res = await apiFetch(
      `/v1/domains/${domainId}/semantic-model/proposals/${proposalId}/${action}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewed_by: reviewedBy }),
      },
    );
    if (!res.ok) {
      return { ok: false, error: await readApiErrorMessage(res) };
    }
    const data: unknown = await res.json();
    if (!isDomainDetail(data)) {
      return { ok: false, error: "Unexpected response shape" };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Request failed" };
  }
}

export const acceptDomainSemanticProposal = createAsyncThunk<
  DomainDetail,
  ReviewSemanticProposalArgs,
  { rejectValue: string }
>(
  "domainDetail/acceptSemanticProposal",
  async ({ domainId, proposalId, reviewedBy }, { rejectWithValue, dispatch }) => {
    const result = await reviewSemanticProposal(domainId, proposalId, reviewedBy, "accept");
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    dispatch(
      showNotification({
        variant: NOTIFICATION_VARIANT.SUCCESS,
        messageKey: "domainDetail.semanticModel.proposals.acceptSucceeded",
        messageNs: "dataHub",
      }),
    );
    return result.data;
  },
);

export const rejectDomainSemanticProposal = createAsyncThunk<
  DomainDetail,
  ReviewSemanticProposalArgs,
  { rejectValue: string }
>(
  "domainDetail/rejectSemanticProposal",
  async ({ domainId, proposalId, reviewedBy }, { rejectWithValue, dispatch }) => {
    const result = await reviewSemanticProposal(domainId, proposalId, reviewedBy, "reject");
    if (!result.ok) {
      return rejectWithValue(result.error);
    }
    dispatch(
      showNotification({
        variant: NOTIFICATION_VARIANT.SUCCESS,
        messageKey: "domainDetail.semanticModel.proposals.rejectSucceeded",
        messageNs: "dataHub",
      }),
    );
    return result.data;
  },
);

/**
 * Every entity, field, relationship, rule, measure, dimension, metric, ontology,
 * glossary term, synonym, and candidate is a proposal until a human reviews it (core
 * doc principle 5).
 *
 * The ``*_format`` and ``*_materiality`` types are field-level: they review one
 * suggested value merged into a single field of an existing item (how it is measured,
 * and what counts as a material move for it), not the item itself.
 */
export type SemanticItemTargetType =
  | "entity"
  | "relationship"
  | "rule"
  | "measure"
  | "dimension"
  | "metric"
  | "synonym"
  | "example"
  | "ontology"
  | "glossary"
  | "measure_format"
  | "dimension_format"
  | "metric_format"
  | "measure_materiality"
  | "metric_materiality"
  | "candidate";

type ReviewSemanticItemArgs = {
  domainId: number;
  targetType: SemanticItemTargetType;
  targetKey: string;
  /** Reviews one field nested inside an entity instead of the entity itself. */
  fieldName?: string;
  reviewedBy: string;
};

async function reviewSemanticItem(
  args: ReviewSemanticItemArgs,
  action: "approve" | "reject",
): Promise<PatchSemanticSectionResult> {
  try {
    const path =
      `/v1/domains/${args.domainId}/semantic-model/items/` +
      `${args.targetType}/${encodeURIComponent(args.targetKey)}/${action}`;
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reviewed_by: args.reviewedBy,
        field_name: args.fieldName ?? null,
      }),
    });
    if (!res.ok) {
      return { ok: false, error: await readApiErrorMessage(res) };
    }
    const data: unknown = await res.json();
    if (!isDomainDetail(data)) {
      return { ok: false, error: "Unexpected response shape" };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Request failed" };
  }
}

export const approveDomainSemanticItem = createAsyncThunk<
  DomainDetail,
  ReviewSemanticItemArgs,
  { rejectValue: string }
>("domainDetail/approveSemanticItem", async (args, { rejectWithValue }) => {
  const result = await reviewSemanticItem(args, "approve");
  if (!result.ok) {
    return rejectWithValue(result.error);
  }
  return result.data;
});

export const rejectDomainSemanticItem = createAsyncThunk<
  DomainDetail,
  ReviewSemanticItemArgs,
  { rejectValue: string }
>("domainDetail/rejectSemanticItem", async (args, { rejectWithValue }) => {
  const result = await reviewSemanticItem(args, "reject");
  if (!result.ok) {
    return rejectWithValue(result.error);
  }
  return result.data;
});

type ReviewSemanticSectionArgs = {
  domainId: number;
  /** ``null`` reviews the entire semantic model (every section, including
   * candidates and pending format proposals) instead of one section. */
  targetType: SemanticItemTargetType | null;
  reviewedBy: string;
};

async function reviewSemanticSection(
  args: ReviewSemanticSectionArgs,
  action: "approve" | "reject",
): Promise<PatchSemanticSectionResult> {
  try {
    const path =
      args.targetType === null
        ? `/v1/domains/${args.domainId}/semantic-model/${action}-all`
        : `/v1/domains/${args.domainId}/semantic-model/sections/${args.targetType}/${action}`;
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reviewed_by: args.reviewedBy }),
    });
    if (!res.ok) {
      return { ok: false, error: await readApiErrorMessage(res) };
    }
    const data: unknown = await res.json();
    if (!isDomainDetail(data)) {
      return { ok: false, error: "Unexpected response shape" };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Request failed" };
  }
}

export const approveDomainSemanticSection = createAsyncThunk<
  DomainDetail,
  ReviewSemanticSectionArgs,
  { rejectValue: string }
>("domainDetail/approveSemanticSection", async (args, { rejectWithValue }) => {
  const result = await reviewSemanticSection(args, "approve");
  if (!result.ok) {
    return rejectWithValue(result.error);
  }
  return result.data;
});

export const rejectDomainSemanticSection = createAsyncThunk<
  DomainDetail,
  ReviewSemanticSectionArgs,
  { rejectValue: string }
>("domainDetail/rejectSemanticSection", async (args, { rejectWithValue }) => {
  const result = await reviewSemanticSection(args, "reject");
  if (!result.ok) {
    return rejectWithValue(result.error);
  }
  return result.data;
});

const domainDetailSlice = createSlice({
  name: "domainDetail",
  initialState: domainDetailInitialState,
  reducers: {
    clearDomainDetail(state) {
      state.status = STATUS.IDLE;
      state.error = null;
      state.domainId = null;
      state.domain = null;
      state.metadata = null;
      state.metadataStatus = STATUS.IDLE;
      state.metadataError = null;
      state.businessContext = null;
      state.businessContextStatus = STATUS.IDLE;
      state.businessContextError = null;
      state.businessContextPatchStatus = STATUS.IDLE;
      state.businessContextPatchError = null;
      state.isRefreshing = false;
      state.entitiesPatchStatus = STATUS.IDLE;
      state.entitiesPatchError = null;
      state.semanticEntitiesPatchStatus = STATUS.IDLE;
      state.semanticEntitiesPatchError = null;
      state.semanticRelationshipsPatchStatus = STATUS.IDLE;
      state.semanticRelationshipsPatchError = null;
      state.semanticMeasuresPatchStatus = STATUS.IDLE;
      state.semanticMeasuresPatchError = null;
      state.semanticMetricsPatchStatus = STATUS.IDLE;
      state.semanticMetricsPatchError = null;
      state.semanticRulesPatchStatus = STATUS.IDLE;
      state.semanticRulesPatchError = null;
      state.semanticGlossaryPatchStatus = STATUS.IDLE;
      state.semanticGlossaryPatchError = null;
      state.semanticOntologiesPatchStatus = STATUS.IDLE;
      state.semanticOntologiesPatchError = null;
      state.semanticCandidatesPatchStatus = STATUS.IDLE;
      state.semanticCandidatesPatchError = null;
      state.publishStatus = STATUS.IDLE;
      state.publishError = null;
      state.rerunStatus = STATUS.IDLE;
      state.rerunError = null;
      state.syncIndexesStatus = STATUS.IDLE;
      state.syncIndexesError = null;
      state.chatState = emptyClarificationChatState;
      state.chatStatus = STATUS.IDLE;
      state.chatError = null;
      state.remainingItems = [];
      state.remainingItemsStatus = STATUS.IDLE;
      state.proposals = [];
      state.proposalsStatus = STATUS.IDLE;
      state.proposalsError = null;
      state.proposalReviewStatus = STATUS.IDLE;
      state.proposalReviewError = null;
      state.itemReviewStatus = STATUS.IDLE;
      state.itemReviewError = null;
      state.sectionReviewStatus = STATUS.IDLE;
      state.sectionReviewError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDomainById.pending, (state, action) => {
        state.status = STATUS.LOADING;
        state.error = null;
        state.domainId = action.meta.arg;
        state.domain = null;
      })
      .addCase(fetchDomainMetadata.pending, (state, action) => {
        state.metadataStatus = STATUS.LOADING;
        state.metadataError = null;
        if (state.domainId !== null && state.domainId !== action.meta.arg) {
          state.metadata = null;
        }
      })
      .addCase(fetchDomainMetadata.fulfilled, (state, action) => {
        if (state.domainId !== null && state.domainId !== action.meta.arg) {
          return;
        }
        state.metadataStatus = STATUS.SUCCEEDED;
        state.metadata = action.payload;
        state.metadataError = null;
      })
      .addCase(fetchDomainMetadata.rejected, (state, action) => {
        state.metadataStatus = STATUS.FAILED;
        state.metadataError = action.payload ?? "Request failed";
      })
      .addCase(fetchDomainBusinessContext.pending, (state) => {
        state.businessContextStatus = STATUS.LOADING;
        state.businessContextError = null;
      })
      .addCase(fetchDomainBusinessContext.fulfilled, (state, action) => {
        state.businessContextStatus = STATUS.SUCCEEDED;
        state.businessContext = action.payload;
        state.businessContextError = null;
      })
      .addCase(fetchDomainBusinessContext.rejected, (state, action) => {
        state.businessContextStatus = STATUS.FAILED;
        state.businessContextError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainBusinessContext.pending, (state) => {
        state.businessContextPatchStatus = STATUS.LOADING;
        state.businessContextPatchError = null;
      })
      .addCase(patchDomainBusinessContext.fulfilled, (state, action) => {
        state.businessContextPatchStatus = STATUS.SUCCEEDED;
        state.businessContext = action.payload;
        state.businessContextPatchError = null;
      })
      .addCase(patchDomainBusinessContext.rejected, (state, action) => {
        state.businessContextPatchStatus = STATUS.FAILED;
        state.businessContextPatchError = action.payload ?? "Request failed";
      })
      .addCase(fetchDomainById.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.domainId = action.payload.id;
        state.domain = action.payload;
        state.error = null;
        const chatFromDomain = clarificationChatFromGeneratedModel(
          action.payload.generated_model,
        );
        if (chatFromDomain !== null) {
          state.chatState = chatFromDomain;
        }
      })
      .addCase(fetchDomainById.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
        state.domain = null;
      })
      .addCase(refreshDomainById.pending, (state) => {
        state.isRefreshing = true;
      })
      .addCase(refreshDomainById.fulfilled, (state, action) => {
        state.isRefreshing = false;
        if (state.domainId !== action.payload.id) {
          return;
        }
        state.domain = action.payload;
        const chatFromDomain = clarificationChatFromGeneratedModel(
          action.payload.generated_model,
        );
        if (chatFromDomain !== null) {
          state.chatState = chatFromDomain;
        }
        if (state.status !== STATUS.SUCCEEDED) {
          state.status = STATUS.SUCCEEDED;
          state.error = null;
        }
      })
      .addCase(refreshDomainById.rejected, (state) => {
        state.isRefreshing = false;
      })
      .addCase(patchDomainDraftEntities.pending, (state) => {
        state.entitiesPatchStatus = STATUS.LOADING;
        state.entitiesPatchError = null;
      })
      .addCase(patchDomainDraftEntities.fulfilled, (state, action) => {
        state.entitiesPatchStatus = STATUS.SUCCEEDED;
        state.entitiesPatchError = null;
        if (
          state.domain !== null &&
          state.domainId === action.meta.arg.domainId
        ) {
          state.domain.draft_entities = action.meta.arg.draftEntities;
        }
      })
      .addCase(patchDomainDraftEntities.rejected, (state, action) => {
        state.entitiesPatchStatus = STATUS.FAILED;
        state.entitiesPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticEntities.pending, (state) => {
        state.semanticEntitiesPatchStatus = STATUS.LOADING;
        state.semanticEntitiesPatchError = null;
      })
      .addCase(patchDomainSemanticEntities.fulfilled, (state, action) => {
        state.semanticEntitiesPatchStatus = STATUS.SUCCEEDED;
        state.semanticEntitiesPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticEntities.rejected, (state, action) => {
        state.semanticEntitiesPatchStatus = STATUS.FAILED;
        state.semanticEntitiesPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticRelationships.pending, (state) => {
        state.semanticRelationshipsPatchStatus = STATUS.LOADING;
        state.semanticRelationshipsPatchError = null;
      })
      .addCase(patchDomainSemanticRelationships.fulfilled, (state, action) => {
        state.semanticRelationshipsPatchStatus = STATUS.SUCCEEDED;
        state.semanticRelationshipsPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticRelationships.rejected, (state, action) => {
        state.semanticRelationshipsPatchStatus = STATUS.FAILED;
        state.semanticRelationshipsPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticMeasures.pending, (state) => {
        state.semanticMeasuresPatchStatus = STATUS.LOADING;
        state.semanticMeasuresPatchError = null;
      })
      .addCase(patchDomainSemanticMeasures.fulfilled, (state, action) => {
        state.semanticMeasuresPatchStatus = STATUS.SUCCEEDED;
        state.semanticMeasuresPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticMeasures.rejected, (state, action) => {
        state.semanticMeasuresPatchStatus = STATUS.FAILED;
        state.semanticMeasuresPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticMetrics.pending, (state) => {
        state.semanticMetricsPatchStatus = STATUS.LOADING;
        state.semanticMetricsPatchError = null;
      })
      .addCase(patchDomainSemanticMetrics.fulfilled, (state, action) => {
        state.semanticMetricsPatchStatus = STATUS.SUCCEEDED;
        state.semanticMetricsPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticMetrics.rejected, (state, action) => {
        state.semanticMetricsPatchStatus = STATUS.FAILED;
        state.semanticMetricsPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticRules.pending, (state) => {
        state.semanticRulesPatchStatus = STATUS.LOADING;
        state.semanticRulesPatchError = null;
      })
      .addCase(patchDomainSemanticRules.fulfilled, (state, action) => {
        state.semanticRulesPatchStatus = STATUS.SUCCEEDED;
        state.semanticRulesPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticRules.rejected, (state, action) => {
        state.semanticRulesPatchStatus = STATUS.FAILED;
        state.semanticRulesPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticGlossary.pending, (state) => {
        state.semanticGlossaryPatchStatus = STATUS.LOADING;
        state.semanticGlossaryPatchError = null;
      })
      .addCase(patchDomainSemanticGlossary.fulfilled, (state, action) => {
        state.semanticGlossaryPatchStatus = STATUS.SUCCEEDED;
        state.semanticGlossaryPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticGlossary.rejected, (state, action) => {
        state.semanticGlossaryPatchStatus = STATUS.FAILED;
        state.semanticGlossaryPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticOntologies.pending, (state) => {
        state.semanticOntologiesPatchStatus = STATUS.LOADING;
        state.semanticOntologiesPatchError = null;
      })
      .addCase(patchDomainSemanticOntologies.fulfilled, (state, action) => {
        state.semanticOntologiesPatchStatus = STATUS.SUCCEEDED;
        state.semanticOntologiesPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticOntologies.rejected, (state, action) => {
        state.semanticOntologiesPatchStatus = STATUS.FAILED;
        state.semanticOntologiesPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticDimensions.pending, (state) => {
        state.semanticDimensionsPatchStatus = STATUS.LOADING;
        state.semanticDimensionsPatchError = null;
      })
      .addCase(patchDomainSemanticDimensions.fulfilled, (state, action) => {
        state.semanticDimensionsPatchStatus = STATUS.SUCCEEDED;
        state.semanticDimensionsPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticDimensions.rejected, (state, action) => {
        state.semanticDimensionsPatchStatus = STATUS.FAILED;
        state.semanticDimensionsPatchError = action.payload ?? "Request failed";
      })
      .addCase(patchDomainSemanticCandidates.pending, (state) => {
        state.semanticCandidatesPatchStatus = STATUS.LOADING;
        state.semanticCandidatesPatchError = null;
      })
      .addCase(patchDomainSemanticCandidates.fulfilled, (state, action) => {
        state.semanticCandidatesPatchStatus = STATUS.SUCCEEDED;
        state.semanticCandidatesPatchError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(patchDomainSemanticCandidates.rejected, (state, action) => {
        state.semanticCandidatesPatchStatus = STATUS.FAILED;
        state.semanticCandidatesPatchError = action.payload ?? "Request failed";
      })
      .addCase(publishDomain.pending, (state, action) => {
        state.publishStatus = STATUS.LOADING;
        state.publishError = null;
        if (state.domain !== null && state.domainId === action.meta.arg.domainId) {
          state.domain.published = true;
          state.domain.entities = action.meta.arg.draftEntities;
          state.domain.draft_entities = action.meta.arg.draftEntities;
          state.domain.generation_status = SEMANTIC_GENERATION_STATUS.PENDING;
          state.domain.generation_stage = "queued";
          state.domain.generation_progress_pct = 5;
          state.domain.generation_error = null;
        }
      })
      .addCase(publishDomain.fulfilled, (state, action) => {
        state.publishStatus = STATUS.SUCCEEDED;
        state.publishError = null;
        if (action.payload.domain !== null) {
          applyDomainDetailPayload(state, action.payload.domain);
          return;
        }
        if (state.domain !== null) {
          state.domain.published = true;
          state.domain.entities = action.payload.draftEntities;
          state.domain.draft_entities = action.payload.draftEntities;
          state.domain.generation_status = SEMANTIC_GENERATION_STATUS.PENDING;
          state.domain.generation_stage = "queued";
          state.domain.generation_progress_pct = 5;
          state.domain.generation_error = null;
        }
      })
      .addCase(publishDomain.rejected, (state, action) => {
        state.publishStatus = STATUS.FAILED;
        state.publishError = action.payload ?? "Request failed";
      })
      .addCase(rerunDomainEnrichment.pending, (state) => {
        state.rerunStatus = STATUS.LOADING;
        state.rerunError = null;
      })
      .addCase(rerunDomainEnrichment.fulfilled, (state, action) => {
        state.rerunStatus = STATUS.SUCCEEDED;
        state.rerunError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(rerunDomainEnrichment.rejected, (state, action) => {
        state.rerunStatus = STATUS.FAILED;
        state.rerunError = action.payload ?? "Request failed";
      })
      .addCase(syncSemanticIndexes.pending, (state) => {
        state.syncIndexesStatus = STATUS.LOADING;
        state.syncIndexesError = null;
      })
      .addCase(syncSemanticIndexes.fulfilled, (state, action) => {
        state.syncIndexesStatus = STATUS.SUCCEEDED;
        state.syncIndexesError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(syncSemanticIndexes.rejected, (state, action) => {
        state.syncIndexesStatus = STATUS.FAILED;
        state.syncIndexesError = action.payload ?? "Request failed";
      })
      .addCase(fetchClarificationChatState.pending, (state) => {
        state.chatStatus = STATUS.LOADING;
        state.chatError = null;
      })
      .addCase(fetchClarificationChatState.fulfilled, (state, action) => {
        state.chatStatus = STATUS.SUCCEEDED;
        state.chatState = action.payload;
        state.chatError = null;
      })
      .addCase(fetchClarificationChatState.rejected, (state, action) => {
        state.chatStatus = STATUS.FAILED;
        state.chatError = action.payload ?? "Request failed";
      })
      .addCase(sendClarificationChatMessage.pending, (state) => {
        state.chatStatus = STATUS.LOADING;
        state.chatError = null;
      })
      .addCase(sendClarificationChatMessage.fulfilled, (state, action) => {
        state.chatStatus = STATUS.SUCCEEDED;
        state.chatState = chatStateFromResponse(action.payload);
        state.chatError = null;
        if (state.domain !== null) {
          state.domain = syncDomainGeneratedModelChat(
            state.domain,
            state.chatState,
            action.payload.document,
          );
        }
      })
      .addCase(sendClarificationChatMessage.rejected, (state, action) => {
        state.chatStatus = STATUS.FAILED;
        state.chatError = action.payload ?? "Request failed";
      })
      .addCase(askAboutTarget.pending, (state) => {
        state.chatStatus = STATUS.LOADING;
        state.chatError = null;
      })
      .addCase(askAboutTarget.fulfilled, (state, action) => {
        state.chatStatus = STATUS.SUCCEEDED;
        state.chatState = chatStateFromResponse(action.payload);
        state.chatError = null;
        if (state.domain !== null) {
          state.domain = syncDomainGeneratedModelChat(
            state.domain,
            state.chatState,
            action.payload.document,
          );
        }
      })
      .addCase(askAboutTarget.rejected, (state, action) => {
        state.chatStatus = STATUS.FAILED;
        state.chatError = action.payload ?? "Request failed";
      })
      .addCase(reviewSkippedQuestions.pending, (state) => {
        state.chatStatus = STATUS.LOADING;
        state.chatError = null;
      })
      .addCase(reviewSkippedQuestions.fulfilled, (state, action) => {
        state.chatStatus = STATUS.SUCCEEDED;
        state.chatState = chatStateFromResponse(action.payload);
        state.chatError = null;
        if (state.domain !== null) {
          state.domain = syncDomainGeneratedModelChat(
            state.domain,
            state.chatState,
            action.payload.document,
          );
        }
      })
      .addCase(reviewSkippedQuestions.rejected, (state, action) => {
        state.chatStatus = STATUS.FAILED;
        state.chatError = action.payload ?? "Request failed";
      })
      .addCase(fetchRemainingItems.pending, (state) => {
        state.remainingItemsStatus = STATUS.LOADING;
      })
      .addCase(fetchRemainingItems.fulfilled, (state, action) => {
        state.remainingItemsStatus = STATUS.SUCCEEDED;
        state.remainingItems = action.payload;
      })
      .addCase(fetchRemainingItems.rejected, (state) => {
        state.remainingItemsStatus = STATUS.FAILED;
      })
      .addCase(fetchDomainSemanticProposals.pending, (state) => {
        state.proposalsStatus = STATUS.LOADING;
        state.proposalsError = null;
      })
      .addCase(fetchDomainSemanticProposals.fulfilled, (state, action) => {
        state.proposalsStatus = STATUS.SUCCEEDED;
        state.proposals = action.payload;
        state.proposalsError = null;
      })
      .addCase(fetchDomainSemanticProposals.rejected, (state, action) => {
        state.proposalsStatus = STATUS.FAILED;
        state.proposalsError = action.payload ?? "Request failed";
      })
      .addCase(acceptDomainSemanticProposal.pending, (state) => {
        state.proposalReviewStatus = STATUS.LOADING;
        state.proposalReviewError = null;
      })
      .addCase(acceptDomainSemanticProposal.fulfilled, (state, action) => {
        state.proposalReviewStatus = STATUS.SUCCEEDED;
        state.proposalReviewError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
        state.proposals = state.proposals.filter(
          (proposal) => proposal.id !== action.meta.arg.proposalId,
        );
      })
      .addCase(acceptDomainSemanticProposal.rejected, (state, action) => {
        state.proposalReviewStatus = STATUS.FAILED;
        state.proposalReviewError = action.payload ?? "Request failed";
      })
      .addCase(rejectDomainSemanticProposal.pending, (state) => {
        state.proposalReviewStatus = STATUS.LOADING;
        state.proposalReviewError = null;
      })
      .addCase(rejectDomainSemanticProposal.fulfilled, (state, action) => {
        state.proposalReviewStatus = STATUS.SUCCEEDED;
        state.proposalReviewError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
        state.proposals = state.proposals.filter(
          (proposal) => proposal.id !== action.meta.arg.proposalId,
        );
      })
      .addCase(rejectDomainSemanticProposal.rejected, (state, action) => {
        state.proposalReviewStatus = STATUS.FAILED;
        state.proposalReviewError = action.payload ?? "Request failed";
      })
      .addCase(approveDomainSemanticItem.pending, (state) => {
        state.itemReviewStatus = STATUS.LOADING;
        state.itemReviewError = null;
      })
      .addCase(approveDomainSemanticItem.fulfilled, (state, action) => {
        state.itemReviewStatus = STATUS.SUCCEEDED;
        state.itemReviewError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(approveDomainSemanticItem.rejected, (state, action) => {
        state.itemReviewStatus = STATUS.FAILED;
        state.itemReviewError = action.payload ?? "Request failed";
      })
      .addCase(rejectDomainSemanticItem.pending, (state) => {
        state.itemReviewStatus = STATUS.LOADING;
        state.itemReviewError = null;
      })
      .addCase(rejectDomainSemanticItem.fulfilled, (state, action) => {
        state.itemReviewStatus = STATUS.SUCCEEDED;
        state.itemReviewError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(rejectDomainSemanticItem.rejected, (state, action) => {
        state.itemReviewStatus = STATUS.FAILED;
        state.itemReviewError = action.payload ?? "Request failed";
      })
      .addCase(approveDomainSemanticSection.pending, (state) => {
        state.sectionReviewStatus = STATUS.LOADING;
        state.sectionReviewError = null;
      })
      .addCase(approveDomainSemanticSection.fulfilled, (state, action) => {
        state.sectionReviewStatus = STATUS.SUCCEEDED;
        state.sectionReviewError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(approveDomainSemanticSection.rejected, (state, action) => {
        state.sectionReviewStatus = STATUS.FAILED;
        state.sectionReviewError = action.payload ?? "Request failed";
      })
      .addCase(rejectDomainSemanticSection.pending, (state) => {
        state.sectionReviewStatus = STATUS.LOADING;
        state.sectionReviewError = null;
      })
      .addCase(rejectDomainSemanticSection.fulfilled, (state, action) => {
        state.sectionReviewStatus = STATUS.SUCCEEDED;
        state.sectionReviewError = null;
        if (state.domainId === action.payload.id) {
          applyDomainDetailPayload(state, action.payload);
        }
      })
      .addCase(rejectDomainSemanticSection.rejected, (state, action) => {
        state.sectionReviewStatus = STATUS.FAILED;
        state.sectionReviewError = action.payload ?? "Request failed";
      });
  },
});

export const { clearDomainDetail } = domainDetailSlice.actions;
export const domainDetailReducer = domainDetailSlice.reducer;
